import { ApiResponse } from "../interfaces/genericResponse/GenericResponse";

/** Plain-language fallbacks when the server sends no readable message. */
export function messageForStatus(status: number): string {
  if (status === 401) return "Your session expired. Sign in again.";
  if (status === 403) return "You don't have permission to do that.";
  if (status === 404) return "We couldn't find what you were looking for.";
  if (status === 409) return "That already exists.";
  if (status === 413) return "That file is too large.";
  if (status === 429) return "Too many attempts. Wait a few minutes and try again.";
  if (status === 503) return "This is unavailable right now. Try again later, and contact support if it keeps happening.";
  if (status >= 500) return "Something went wrong on our side. Try again, and contact support if it keeps happening.";
  return "We couldn't complete that request. Check the details and try again.";
}

export const NETWORK_ERROR_MESSAGE = "Can't reach Waivera. Check your connection and try again.";

/**
 * Reads a Waivera API response without ever surfacing a parser error: empty bodies,
 * HTML error pages and ASP.NET problem details all become a readable ApiResponse.
 */
export async function readApiResponse<T>(response: Response): Promise<ApiResponse<T>> {
  let body: any = null;
  try {
    const text = await response.text();
    body = text ? JSON.parse(text) : null;
  } catch {
    body = null;
  }

  const serverMessage = readServerMessage(body);

  if (body && typeof body === "object" && "success" in body && "statusCode" in body) {
    return {
      ...body,
      message: serverMessage ?? (response.ok ? body.message : messageForStatus(response.status)),
    } as ApiResponse<T>;
  }

  return {
    data: (body?.data ?? null) as T,
    statusCode: response.status,
    success: response.ok,
    message: serverMessage ?? (response.ok ? "" : messageForStatus(response.status)),
  };
}

function readServerMessage(body: any): string | null {
  if (!body || typeof body !== "object") return null;
  if (typeof body.message === "string" && body.message.trim()) return body.message.trim();
  // ASP.NET validation problem details: { errors: { Field: ["message"] } }
  if (body.errors && typeof body.errors === "object") {
    for (const value of Object.values(body.errors)) {
      const first = Array.isArray(value) ? value[0] : value;
      if (typeof first === "string" && first.trim()) return first.trim();
    }
  }
  return null;
}
